import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpClient, HttpParams} from '@angular/common/http';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {DepartmentDto, DepartmentService} from './department-service';
import {DepartmentModal} from './department-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {firstValueFrom} from 'rxjs';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';
import {ProvinceDto} from '../../../../generated-model/models';

@Component({
  selector: 'app-department',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './department.html',
  styleUrl: './department.scss',
})
export class DepartmentComponent implements OnInit {
  private readonly service = inject(DepartmentService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);

  protected readonly term = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly provinceFilter = signal<number | null>(null);
  protected readonly provinces = signal<ProvinceDto[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<DepartmentDto[]>([]);

  private totalElements = 0;

  constructor() {
  }

  ngOnInit(): void {
    this.load();
    this.loadProvinces();
  }

  private loadProvinces(): void {
    this.http.get<ProvinceDto[]>('/api/master/provinces/active').subscribe(provinces => this.provinces.set(provinces));
  }

  load(): void {
    this.isLoading.set(true);
    let params = new HttpParams().set('page', this.page() - 1).set('size', this.pageSize());
    if (this.term()) {
      params = params.set('search', this.term());
    }
    if (this.stateFilter() != null) {
      params = params.set('state', this.stateFilter()!);
    }
    if (this.provinceFilter() != null) {
      params = params.set('province', this.provinceFilter()!);
    }
    this.service.search(params).subscribe({
        next: (data) => {
          this.displayedItems.set(data.content);
          this.totalElements = resolveTotalElements(data);
          this.collectionSize.set(this.totalElements);
          this.isLoading.set(false);
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoading.set(false);
          this.cdr.detectChanges();
        }
      });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.term.set('');
    this.stateFilter.set(null);
    this.provinceFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(DepartmentModal, {size: 'lg'});
    modalRef.componentInstance.onSave = async (result: DepartmentDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createDepartment(result));
        this.toastService.success('บันทึกข้อมูลสำเร็จ');
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: DepartmentDto): void {
    const modalRef = this.modalService.open(DepartmentModal, {size: 'lg'});
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.onSave = async (result: DepartmentDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateDepartment(result.id, result));
        this.toastService.success('บันทึกข้อมูลสำเร็จ');
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: DepartmentDto): void {
    const modalRef = this.modalService.open(DepartmentModal, {size: 'lg'});
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteDepartment(id).subscribe({
      next: () => {
        this.toastService.success('ลบข้อมูลสำเร็จ');
        this.load();
      }
    });
  }
}
